"use client";

import { type ReactNode, useEffect } from "react";

export interface SlideOverProps {
  /** Whether the panel is open. */
  open: boolean;
  /** Called when the scrim is clicked or Escape is pressed. */
  onClose: () => void;
  /** Panel contents. Kept mounted so the close animation can play out; pass a
   *  cached copy of the last content if yours unmounts on close. */
  children: ReactNode;
  /** Panel width in px. Defaults to 392. */
  width?: number;
  /** Accessible name for the dialog. */
  label?: string;
}

/**
 * A right-side slide-over panel with a subtle, theme-aware scrim over
 * everything behind it. Clicking the scrim or pressing Escape closes it, so the
 * panel is dismissable without hunting for a close button.
 *
 * Positioning: renders absolutely, so its nearest positioned ancestor must be
 * `position: relative` (usually with `overflow: hidden`). The scrim and panel
 * are kept mounted and toggle by class, so both open and close ease smoothly.
 */
export function SlideOver({
  open,
  onClose,
  children,
  width = 392,
  label,
}: SlideOverProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close panel"
        tabIndex={-1}
        aria-hidden={!open}
        onClick={onClose}
        className={[
          "absolute inset-0 z-20 cursor-default border-0 bg-(--scrim) transition-opacity duration-300 ease-in-out motion-reduce:transition-none",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-hidden={!open}
        style={{ width }}
        className={[
          "absolute inset-y-0 right-0 z-30 flex flex-col overflow-y-auto border-l border-(--divider-region) bg-(--bg-white) shadow-xl",
          "transition-[translate,opacity] duration-300 ease-in-out motion-reduce:transition-none",
          open
            ? "translate-x-0 opacity-100"
            : "pointer-events-none translate-x-full opacity-0",
        ].join(" ")}
      >
        {children}
      </aside>
    </>
  );
}
